import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AnalyzersService } from 'src/app/providers/services/analyzers.service';
import { ConfirmationModalService } from 'src/app/providers/services/confirmation-modal.service';
import { AnalyzerMission } from 'src/app/storage/models/analyzer-mission.model';
import { Analyzer } from 'src/app/storage/models/analyzer.model';
import { Mission } from 'src/app/storage/models/mission.model';

export interface AnalyzerSelection {
  analyzer: Analyzer;
  handleFlyingH2?: boolean;
}

@Component({
    selector: 'app-add-analyzer',
    templateUrl: './add-analyzer.component.html',
    styleUrls: ['./add-analyzer.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddAnalyzerComponent implements OnInit, OnDestroy {
  analyzers: Analyzer[] = [];
  displayedAnalyzers: Analyzer[] = [];

  @Input() mission: Mission;
  @Input() missionAnalyzer: AnalyzerMission;
  @Output() closeModal = new EventEmitter<AnalyzerSelection>();

  private analyzersSubscription: Subscription;

  constructor(
    private analyzersService: AnalyzersService,
    private confirmationModalService: ConfirmationModalService,
    private translateService: TranslateService
  ) { }

  async ngOnInit(): Promise<void> {
    this.analyzersSubscription = this.analyzersService.analyzers$.subscribe((analyzers: Analyzer[]) => {
      const missionAnalyzers = this.analyzersService.missionAnalyzers$.getValue();

      this.analyzers = analyzers
        // Remove selected analyzer
        .filter(analyzer => missionAnalyzers.find(missionAnalyzer => analyzer.analyzerId === missionAnalyzer.analyzerId) === undefined)
        // Remove deactivated analyzers
        .filter(analyzer => analyzer.isActivated);
        
      this.displayedAnalyzers = this.analyzers;
    });
  }

  ngOnDestroy(): void {
    if (this.analyzersSubscription) { this.analyzersSubscription.unsubscribe(); }
  }

  close(): void {
    this.confirmationModalService.closeModal();
    this.closeModal.emit();
  }

  addAnalyzer(analyzer: Analyzer): void {
    if (this.shouldAskHandleFlyingH2(analyzer)) {
      this.openHandleFlyingH2Modal(analyzer);
      return;
    }

    this.closeModal.emit({ analyzer });
  }

  private shouldAskHandleFlyingH2(analyzer: Analyzer): boolean {
    const modelName = (analyzer.analyzermodelName ?? '').toLowerCase();
    const isMetler = modelName.includes('metler') || modelName.includes('mettler');
    const hasH2 = (analyzer.analyzermodelGas ?? '').split(' ').find(gas => gas === 'H2') != null;
    return isMetler && hasH2;
  }

  private async openHandleFlyingH2Modal(analyzer: Analyzer): Promise<void> {
    const translations = await this.translateService.get('MATERIAL.ANALYZER').toPromise();
    const yes = await this.translateService.get('YES').toPromise();
    const no = await this.translateService.get('NO').toPromise();

    this.confirmationModalService.openModal({
      title: `<strong>${translations['HANDLE_FLYING_H2_QUESTION']}</strong>`,
      buttons: [
        {
          text: yes,
          method: async () => {
            this.confirmationModalService.closeModal();
            this.closeModal.emit({ analyzer, handleFlyingH2: true });
          }
        },
        {
          text: no,
          method: async () => {
            this.confirmationModalService.closeModal();
            this.closeModal.emit({ analyzer });
          }
        }
      ]
    });
  }

  filterAnalyzers($event): void {
    const value = $event.target.value;

    this.displayedAnalyzers = this.analyzers.filter(analyzer => {
      // List attribute to filter on
      const attributesToFilterOn = [
        analyzer.analyzerId,
        analyzer.analyzermodelName
      ];

      // Find at least one attribute that checks the criterion
      return attributesToFilterOn.find(attribute =>
        attribute.toUpperCase().includes(value.toUpperCase())
      ) != null;
    });
  }

}
